"use client";

import {
  EllipsisIcon,
  FileIcon,
  LockIcon,
  MessageSquareReplyIcon,
  PencilIcon,
  PinIcon,
  PinOffIcon,
  SmilePlusIcon,
  Trash2Icon,
} from "lucide-react";
import * as React from "react";

import { cn } from "../../cn";
import { AvatarStack, AvatarTile } from "../avatar-tile";
import { Badge } from "../badge";
import { Button } from "../button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../dropdown-menu";
import { EmptyState } from "../empty-state";
import { FOCUS_RING } from "../focus-ring";
import { HOVER_EASE } from "../states";
import { WorkerBadge, workerName } from "../worker-badge";
import {
  Attachment,
  AttachmentContent,
  AttachmentDescription,
  AttachmentGroup,
  AttachmentMedia,
  AttachmentTitle,
  AttachmentTrigger,
} from "./attachment";
import { EmojiPickerPopover } from "./emoji-picker";
import { dayLabel, fullDateOf, relativeTime, sameDay, timeOf } from "./format";
import { Marker, MarkerContent } from "./marker";
import { Message, MessageAvatar, MessageContent, MessageHeader } from "./message";
import {
  MessageScroller,
  MessageScrollerButton,
  MessageScrollerContent,
  MessageScrollerItem,
  MessageScrollerProvider,
  MessageScrollerViewport,
} from "./message-scroller";
import { toAvatar } from "./person";
import type { ChatAttachment, ChatMessage, ChatPerson, ChatReaction } from "./types";
import { useStickToBottom } from "./use-stick-to-bottom";
import { useWindowedRows } from "./windowed-rows";

// The conversation body of Chat mode and the task drawer (Work OS comm-rail.tsx layout, DTP
// look): a flat list, not bubbles. Messages from one author within five minutes on the same day
// fold into one block (Slack/Zulip grouping; Work OS repeats every header). Day dividers and the
// "New" line are DTP `Marker`s; the hover bar (Slack's: quick reactions, Reply in thread, the
// app's extras, then a "…" menu with pin, the app's items, edit and delete; CHAT-CONTROLS)
// overlays the row so nothing reflows, shows on focus-within too and always on coarse
// pointers. The scroller is DTP's `MessageScroller` (stays at the end as messages arrive, keeps
// position on prepend).

const GROUP_WINDOW_MS = 5 * 60_000;
const DEFAULT_REACTIONS = ["👍", "✅", "❤️", "👀"];

export type MessageListDivider = { afterId: string; label: React.ReactNode };

export type MessageListProps = Omit<React.ComponentProps<"div">, "children"> & {
  messages: ChatMessage[];
  /** The viewer: their own messages get Edit and Delete. */
  currentUserId?: string | undefined;
  /** Draws the "New" line before the first message after this one. */
  lastReadId?: string | undefined;
  /** Extra captioned lines (e.g. "3 replies" under a thread root). */
  dividers?: MessageListDivider[] | undefined;
  /** Fixed clock for the day captions (tests, screenshots); defaults to now. */
  now?: Date | undefined;
  locale?: string | undefined;
  quickReactions?: string[] | undefined;
  onReact?: ((messageId: string, emoji: string) => void) | undefined;
  onOpenThread?: ((messageId: string) => void) | undefined;
  onPinChange?: ((messageId: string, pinned: boolean) => void) | undefined;
  onEdit?: ((messageId: string) => void) | undefined;
  onDelete?: ((messageId: string) => void) | undefined;
  /** More buttons for the hover bar, after Reply in thread (save). */
  extraActions?: ((message: ChatMessage) => React.ReactNode) | undefined;
  /**
   * Items for the row's "…" menu (`DropdownMenuItem`s: copy link, make a task), between Pin and
   * Edit / Delete. The menu shows whenever it has an item.
   */
  menuItems?: ((message: ChatMessage) => React.ReactNode) | undefined;
  hasEarlier?: boolean | undefined;
  loadingEarlier?: boolean | undefined;
  onLoadEarlier?: (() => void) | undefined;
  /** Shown when there are no messages. */
  empty?: React.ReactNode;
  /** Name of the scroll region. */
  label?: string | undefined;
  /** Sends a `failed` optimistic message again (CHAT-PERF). */
  onRetry?: ((messageId: string) => void) | undefined;
  /** Rows beyond which only the rows near the viewport render (default 150). */
  virtualizeAfter?: number | undefined;
  /**
   * CHAT-DM: wraps a message's author avatar and name (each a `button` trigger) — the app opens
   * the author's profile popover from it. Return undefined to leave an author plain.
   */
  /**
   * CHAT-SWITCH-B: the message a link points at — scrolled into view once and marked
   * (`data-highlighted`) so the eye finds it.
   */
  highlightId?: string | undefined;
  renderAuthor?:
    | ((author: ChatPerson, trigger: React.ReactElement) => React.ReactNode | undefined)
    | undefined;
};

/** CHAT-PERF: a list longer than this renders a window of rows (windowed-rows.ts). */
const VIRTUALIZE_AFTER = 150;

type Row =
  | { kind: "day"; key: string; label: string }
  | { kind: "new"; key: string }
  | { kind: "divider"; key: string; label: React.ReactNode }
  | { kind: "message"; key: string; message: ChatMessage; continued: boolean };

function buildRows(
  messages: ChatMessage[],
  now: Date,
  locale: string | undefined,
  lastReadId: string | undefined,
  dividers: MessageListDivider[],
): Row[] {
  const rows: Row[] = [];
  const after = new Map(dividers.map((d) => [d.afterId, d.label]));
  const readIndex = lastReadId ? messages.findIndex((m) => m.id === lastReadId) : -1;
  let prev: ChatMessage | undefined;
  let breakGroup = false;
  messages.forEach((m, i) => {
    if (!prev || !sameDay(prev.createdAt, m.createdAt)) {
      rows.push({ kind: "day", key: `day-${m.id}`, label: dayLabel(m.createdAt, now, locale) });
      breakGroup = true;
    }
    if (readIndex >= 0 && i === readIndex + 1) {
      rows.push({ kind: "new", key: `new-${m.id}` });
      breakGroup = true;
    }
    const continued =
      !breakGroup &&
      prev !== undefined &&
      !prev.system &&
      !m.system &&
      prev.author.id === m.author.id &&
      new Date(m.createdAt).getTime() - new Date(prev.createdAt).getTime() < GROUP_WINDOW_MS;
    rows.push({ kind: "message", key: m.id, message: m, continued });
    breakGroup = false;
    const label = after.get(m.id);
    if (label !== undefined) {
      rows.push({ kind: "divider", key: `div-${m.id}`, label });
      breakGroup = true;
    }
    prev = m;
  });
  return rows;
}

export function MessageList({
  messages,
  currentUserId,
  lastReadId,
  dividers = [],
  now,
  locale,
  quickReactions = DEFAULT_REACTIONS,
  onReact,
  onOpenThread,
  onPinChange,
  onEdit,
  onDelete,
  extraActions,
  menuItems,
  hasEarlier = false,
  loadingEarlier = false,
  onLoadEarlier,
  empty,
  label = "Messages",
  onRetry,
  virtualizeAfter = VIRTUALIZE_AFTER,
  renderAuthor,
  highlightId,
  className,
  ...props
}: MessageListProps) {
  const clock = now ?? new Date();
  const rows = buildRows(messages, clock, locale, lastReadId, dividers);
  const actions = {
    quickReactions,
    onReact,
    onOpenThread,
    onPinChange,
    onEdit,
    onDelete,
    extraActions,
    menuItems,
    onRetry,
    renderAuthor,
  };
  // CHAT-PERF: long lists render only the rows near the viewport (windowed-rows.ts).
  const viewport = React.useRef<HTMLDivElement | null>(null);
  // Rows are rebuilt every render; the window only cares when their keys change.
  const keySig = rows.map((r) => r.key).join("\n");
  const keys = React.useMemo(() => (keySig ? keySig.split("\n") : []), [keySig]);
  const rowsRef = React.useRef(rows);
  rowsRef.current = rows;
  const estimate = React.useCallback((i: number) => {
    const r = rowsRef.current[i];
    return r?.kind === "message" ? (r.continued ? 30 : 64) : 40;
  }, []);
  const virtual = rows.length > virtualizeAfter;
  // CHAT-AUTOSCROLL: stay on the newest message while the reader is near the bottom; a pill when
  // they are up in history and something new arrived.
  const idSig = messages.map((m) => m.id).join("\n");
  const ids = React.useMemo(() => (idSig ? idSig.split("\n") : []), [idSig]);
  const mineSig = currentUserId
    ? messages
        .filter((m) => m.author.id === currentUserId)
        .map((m) => m.id)
        .join("\n")
    : "";
  const mineIds = React.useMemo(() => new Set(mineSig ? mineSig.split("\n") : []), [mineSig]);
  const stick = useStickToBottom({ viewport, ids, mineIds });
  const windowed = useWindowedRows({ keys, estimate, enabled: virtual, viewport });
  // CHAT-SWITCH-B: a linked message comes into view once it is in the list (not again on
  // updates). Outside a long list's rendered window, the window first moves onto it; this runs
  // again as the window changes, until the row has mounted and is centred.
  const shownHighlight = React.useRef<string | null>(null);
  const highlightListed = highlightId ? keys.includes(highlightId) : false;
  const { scrollToKey } = windowed;
  const rangeSig = `${windowed.range.start}:${windowed.range.end}`;
  React.useEffect(() => {
    void rangeSig;
    if (!highlightId || !highlightListed || shownHighlight.current === highlightId) return;
    // Message ids are uuids (no quote to escape); anything else is not looked for.
    if (!/^[\w-]+$/.test(highlightId)) return;
    const el = viewport.current?.querySelector<HTMLElement>(`[data-message-id="${highlightId}"]`);
    if (!el) {
      if (virtual) scrollToKey(highlightId);
      return;
    }
    shownHighlight.current = highlightId;
    el.scrollIntoView?.({ block: "center" });
  }, [highlightId, highlightListed, virtual, scrollToKey, rangeSig]);
  const renderRow = (row: Row) => {
    if (row.kind === "day")
      return (
        <Marker key={row.key} variant="separator" className="my-3">
          <MarkerContent className="font-medium text-secondary">{row.label}</MarkerContent>
        </Marker>
      );
    if (row.kind === "new")
      return (
        <Marker
          key={row.key}
          variant="separator"
          data-slot="message-list-new"
          className="my-2 text-primary-text before:bg-primary after:bg-primary"
        >
          <MarkerContent className="font-semibold">New</MarkerContent>
        </Marker>
      );
    if (row.kind === "divider")
      return (
        <Marker key={row.key} variant="separator" className="my-3">
          <MarkerContent>{row.label}</MarkerContent>
        </Marker>
      );
    const marked = row.message.id === highlightId;
    const row_ = (
      <MessageRow
        message={row.message}
        continued={row.continued}
        mine={row.message.author.id === currentUserId}
        now={clock}
        locale={locale}
        {...actions}
      />
    );
    const body = marked ? (
      <div
        data-highlighted="true"
        className="-mx-2 rounded-md bg-primary/10 px-2 ring-1 ring-primary/30"
      >
        {row_}
      </div>
    ) : (
      row_
    );
    return virtual ? (
      <div
        key={row.key}
        data-message-id={row.message.id}
        className={row.continued ? "pt-0.5" : "pt-3"}
      >
        {body}
      </div>
    ) : (
      <MessageScrollerItem
        key={row.key}
        messageId={row.message.id}
        // The window's anchor, should the list grow past the windowing threshold.
        data-row-key={row.key}
        className={row.continued ? "mt-0.5" : "mt-3 first:mt-0"}
      >
        {body}
      </MessageScrollerItem>
    );
  };
  return (
    <div
      data-slot="message-list"
      className={cn("relative flex min-h-0 flex-1 flex-col", className)}
      {...props}
    >
      <MessageScrollerProvider defaultScrollPosition="end">
        <MessageScroller>
          <MessageScrollerViewport aria-label={label} ref={viewport}>
            <MessageScrollerContent className="gap-0 px-4 py-4 max-md:px-3">
              {hasEarlier && onLoadEarlier ? (
                <div className="flex justify-center pb-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onLoadEarlier}
                    disabled={loadingEarlier}
                  >
                    {loadingEarlier ? "Loading earlier messages…" : "Load earlier messages"}
                  </Button>
                </div>
              ) : null}
              {messages.length === 0 ? (
                <div className="my-auto">
                  {empty ?? (
                    <EmptyState
                      variant="compact"
                      title="No messages yet"
                      description="Start the conversation below."
                    />
                  )}
                </div>
              ) : null}
              {virtual ? (
                <div
                  ref={windowed.blockRef}
                  data-slot="message-list-window"
                  data-rendered={windowed.range.end - windowed.range.start}
                  className="flex flex-col"
                >
                  <div aria-hidden={true} style={{ height: windowed.before }} />
                  {rows.slice(windowed.range.start, windowed.range.end).map((row) => (
                    // flow-root: the row's own margins stay inside the measured box.
                    <div
                      key={row.key}
                      ref={windowed.measureRef(row.key)}
                      data-row-key={row.key}
                      className="flow-root"
                    >
                      {renderRow(row)}
                    </div>
                  ))}
                  <div aria-hidden={true} style={{ height: windowed.after }} />
                </div>
              ) : (
                rows.map(renderRow)
              )}
            </MessageScrollerContent>
          </MessageScrollerViewport>
          {stick.unseen > 0 ? (
            <button
              type="button"
              data-slot="message-list-new-pill"
              onClick={stick.jumpToBottom}
              className={cn(
                "absolute bottom-4 left-1/2 z-10 -translate-x-1/2 rounded-full bg-primary px-3 py-1 text-xs font-medium text-primary-foreground shadow-md",
                FOCUS_RING,
              )}
            >
              New messages ↓
            </button>
          ) : (
            <MessageScrollerButton />
          )}
        </MessageScroller>
      </MessageScrollerProvider>
    </div>
  );
}

type RowActions = Pick<
  MessageListProps,
  | "quickReactions"
  | "onReact"
  | "onOpenThread"
  | "onPinChange"
  | "onEdit"
  | "onDelete"
  | "extraActions"
  | "menuItems"
  | "onRetry"
  | "renderAuthor"
>;

const AUTHOR_TRIGGER = cn("cursor-pointer rounded-md border-0 bg-transparent p-0", FOCUS_RING);

function MessageRow({
  message: m,
  continued,
  mine,
  now,
  locale,
  quickReactions = DEFAULT_REACTIONS,
  onReact,
  onOpenThread,
  onPinChange,
  onEdit,
  onDelete,
  extraActions,
  menuItems,
  onRetry,
  renderAuthor,
}: RowActions & {
  message: ChatMessage;
  continued: boolean;
  mine: boolean;
  now: Date;
  locale?: string | undefined;
}) {
  if (m.system) {
    // A system message with a card (a needs-you ask, an approval) shows the card itself, with its
    // buttons: never just its words as a grey line (SCREEN-HANDOFF: the line had no action).
    if (m.embed)
      return (
        <div
          data-slot="message-system"
          data-message-id={m.id}
          data-card=""
          className="flex flex-col gap-1 py-1 pl-[42px]"
        >
          <div className="max-w-xl">{m.embed}</div>
          <time
            dateTime={m.createdAt}
            title={fullDateOf(m.createdAt, locale)}
            className="text-xs text-muted-foreground"
          >
            {timeOf(m.createdAt, locale)}
          </time>
        </div>
      );
    return (
      <div
        data-slot="message-system"
        className="flex items-baseline gap-2 py-0.5 pl-[42px] text-xs text-muted-foreground"
      >
        <span className="min-w-0">{m.body}</span>
        <time dateTime={m.createdAt} title={fullDateOf(m.createdAt, locale)}>
          {timeOf(m.createdAt, locale)}
        </time>
      </div>
    );
  }
  // An optimistic message has no server row yet: nothing to react to, pin or edit.
  const local = m.status !== undefined;
  const canEdit = !local && mine && m.editable !== false && Boolean(onEdit);
  const canDelete = !local && mine && Boolean(onDelete);
  const extra = local ? null : extraActions?.(m);
  const items = local ? null : menuItems?.(m);
  const hasMenu = !local && Boolean(onPinChange || items || canEdit || canDelete);
  const hasBar = !local && Boolean(onReact || onOpenThread || extra || hasMenu);
  const authorName = m.author.workerKind ? workerName(m.author.name) : m.author.name;
  return (
    <Message
      data-message-id={m.id}
      data-continued={continued || undefined}
      data-pinned={m.pinned || undefined}
      data-status={m.status}
      aria-label={continued ? undefined : `${m.author.name}, ${timeOf(m.createdAt, locale)}`}
      aria-busy={m.status === "sending" || undefined}
      className={cn(
        "-mx-2 rounded-md px-2 py-1 hover:bg-sunken focus-within:bg-sunken data-pinned:bg-warning-soft/60 data-[status=sending]:opacity-60",
        HOVER_EASE,
      )}
    >
      <MessageAvatar>
        {continued ? (
          <time
            dateTime={m.createdAt}
            title={fullDateOf(m.createdAt, locale)}
            className="pt-0.5 text-[10px] text-muted-foreground tabular-nums opacity-0 group-hover/message:opacity-100 group-focus-within/message:opacity-100"
          >
            {timeOf(m.createdAt, locale)}
          </time>
        ) : (
          (renderAuthor?.(
            m.author,
            <button
              type="button"
              data-slot="message-author-avatar"
              aria-label={`${authorName}: profile`}
              className={cn(AUTHOR_TRIGGER, "flex")}
            >
              <AvatarTile {...toAvatar(m.author)} size="lg" />
            </button>,
          ) ?? <AvatarTile {...toAvatar(m.author)} size="lg" />)
        )}
      </MessageAvatar>
      <MessageContent>
        {continued ? null : (
          <MessageHeader>
            {renderAuthor?.(
              m.author,
              <button
                type="button"
                data-slot="message-author"
                className={cn(
                  AUTHOR_TRIGGER,
                  "min-w-0 truncate text-sm font-semibold text-foreground hover:underline",
                )}
              >
                {authorName}
              </button>,
            ) ?? (
              <span className="truncate text-sm font-semibold text-foreground">{authorName}</span>
            )}
            {m.author.status ? (
              <span
                role="img"
                aria-label={`Status: ${m.author.status.text ?? m.author.status.emoji}`}
                title={m.author.status.text}
                data-slot="author-status"
                className="shrink-0 text-[13px] leading-none"
              >
                {m.author.status.emoji}
              </span>
            ) : null}
            {m.author.workerKind ? (
              <WorkerAuthorBadges kind={m.author.workerKind} badge={m.author.badge} />
            ) : m.author.kind === "agent" ? (
              <Badge variant="accent">{m.author.badge ?? "Agent"}</Badge>
            ) : m.author.badge ? (
              // CHAT-SWITCH-B: a person-like author with a label ("Integration").
              <Badge variant="outline" data-slot="author-badge">
                {m.author.badge}
              </Badge>
            ) : null}
            {m.peopleOnly ? (
              <Badge variant="warning">
                <LockIcon aria-hidden={true} />
                People only
              </Badge>
            ) : null}
            {m.pinned ? (
              <span className="inline-flex items-center gap-1 text-warning-text">
                <PinIcon aria-hidden={true} className="size-3" />
                Pinned
              </span>
            ) : null}
            <time
              dateTime={m.createdAt}
              title={fullDateOf(m.createdAt, locale)}
              className="tabular-nums"
            >
              {timeOf(m.createdAt, locale)}
            </time>
            {m.meta}
          </MessageHeader>
        )}
        <CollapsibleBody>
          {m.body}
          {m.edited ? <span className="ml-1 text-xs text-muted-foreground">(edited)</span> : null}
        </CollapsibleBody>
        {continued && m.meta ? (
          <div
            data-slot="message-meta"
            className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground"
          >
            {m.meta}
          </div>
        ) : null}
        {m.attachments?.length ? <MessageAttachments items={m.attachments} /> : null}
        {m.files ? <div data-slot="message-files">{m.files}</div> : null}
        {m.embed ? <div className="mt-1 max-w-xl">{m.embed}</div> : null}
        {m.status === "failed" ? (
          <div
            data-slot="message-failed"
            role="alert"
            className="mt-0.5 flex items-center gap-2 text-xs text-danger-text"
          >
            Not sent.
            {onRetry ? (
              <Button
                variant="link"
                size="xs"
                className="h-5 px-0 text-danger-text"
                onClick={() => onRetry(m.id)}
              >
                Retry
              </Button>
            ) : null}
          </div>
        ) : m.status === "sending" ? (
          <span className="sr-only">Sending</span>
        ) : null}
        {m.reactions?.length ? (
          <Reactions messageId={m.id} reactions={m.reactions} onReact={onReact} />
        ) : null}
        {m.thread && (m.thread.replyCount > 0 || m.thread.replying?.length) ? (
          <ThreadSummary message={m} now={now} locale={locale} onOpenThread={onOpenThread} />
        ) : null}
      </MessageContent>
      {hasBar ? (
        <fieldset
          data-slot="message-actions"
          aria-label={`Actions for the message from ${m.author.name}`}
          className="absolute -top-3 right-2 z-10 m-0 flex items-center gap-0.5 rounded-md border border-border bg-popover p-0.5 opacity-0 shadow-sm transition-opacity duration-(--dur-fast) group-hover/message:opacity-100 group-focus-within/message:opacity-100 has-[[data-popup-open]]:opacity-100 pointer-coarse:opacity-100"
        >
          {onReact
            ? quickReactions.map((emoji) => (
                <Button
                  key={emoji}
                  variant="ghost"
                  size="icon-xs"
                  aria-label={`React with ${emoji}`}
                  onClick={() => onReact(m.id, emoji)}
                >
                  <span aria-hidden={true}>{emoji}</span>
                </Button>
              ))
            : null}
          {/* CHAT-SWITCH-A: every emoji (search, recents, skin tone), not only the quick four. */}
          {onReact ? (
            <EmojiPickerPopover label="Add reaction" onPick={(emoji) => onReact(m.id, emoji)} />
          ) : null}
          {onOpenThread ? (
            <Button
              variant="ghost"
              size="icon-xs"
              aria-label="Reply in thread"
              title="Reply in thread"
              onClick={() => onOpenThread(m.id)}
            >
              <MessageSquareReplyIcon />
            </Button>
          ) : null}
          {extra}
          {hasMenu ? (
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    aria-label="More actions"
                    title="More actions"
                  />
                }
              >
                <EllipsisIcon />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-44">
                {onPinChange ? (
                  <DropdownMenuItem onClick={() => onPinChange(m.id, !m.pinned)}>
                    {m.pinned ? <PinOffIcon /> : <PinIcon />}
                    {m.pinned ? "Unpin" : "Pin"}
                  </DropdownMenuItem>
                ) : null}
                {items}
                {(canEdit && onEdit) || canDelete ? <DropdownMenuSeparator /> : null}
                {canEdit && onEdit ? (
                  <DropdownMenuItem onClick={() => onEdit(m.id)}>
                    <PencilIcon />
                    Edit
                  </DropdownMenuItem>
                ) : null}
                {canDelete && onDelete ? (
                  <DropdownMenuItem variant="destructive" onClick={() => onDelete(m.id)}>
                    <Trash2Icon />
                    Delete
                  </DropdownMenuItem>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </fieldset>
      ) : null}
    </Message>
  );
}

/**
 * WF-FIX (2026-10-26): a workforce author's badges — the `WorkerBadge` (DE / AG), then whatever
 * the app's badge says beyond the kind ("DE · Recruiting coordinator" → "Recruiting
 * coordinator"), so nothing reads "DE · DE".
 */
function WorkerAuthorBadges({ kind, badge }: { kind: "de" | "ag"; badge?: string | undefined }) {
  const rest = badge ? workerName(badge).trim() : "";
  const extra = rest && rest !== "DE" && rest !== "AG" ? rest : null;
  return (
    <>
      <WorkerBadge kind={kind} size="sm" />
      {extra ? <Badge variant="outline">{extra}</Badge> : null}
    </>
  );
}

function MessageAttachments({ items }: { items: ChatAttachment[] }) {
  return (
    <AttachmentGroup className="mt-1">
      {items.map((a) => (
        <Attachment key={a.id} size="sm" state={a.state ?? "done"}>
          <AttachmentMedia variant={a.thumbnailUrl ? "image" : "icon"}>
            {a.thumbnailUrl ? <img src={a.thumbnailUrl} alt="" /> : <FileIcon aria-hidden={true} />}
          </AttachmentMedia>
          <AttachmentContent>
            <AttachmentTitle>{a.name}</AttachmentTitle>
            {a.description ? <AttachmentDescription>{a.description}</AttachmentDescription> : null}
          </AttachmentContent>
          {a.href ? (
            <AttachmentTrigger render={<a href={a.href} aria-label={`Open ${a.name}`} />} />
          ) : null}
        </Attachment>
      ))}
    </AttachmentGroup>
  );
}

function Reactions({
  messageId,
  reactions,
  onReact,
}: {
  messageId: string;
  reactions: ChatReaction[];
  onReact?: ((messageId: string, emoji: string) => void) | undefined;
}) {
  return (
    <div data-slot="message-reactions" className="mt-1 flex flex-wrap gap-1">
      {reactions.map((r) => {
        const label = `${r.emoji} ${r.count}${r.reacted ? ", you reacted" : ""}${
          r.names?.length ? `: ${r.names.join(", ")}` : ""
        }`;
        const cls = cn(
          "inline-flex h-6 items-center gap-1 rounded-full border px-2 text-xs tabular-nums",
          r.reacted
            ? "border-primary bg-primary-soft text-primary-text"
            : "border-border bg-card text-secondary",
        );
        return onReact ? (
          <button
            key={r.emoji}
            type="button"
            aria-pressed={r.reacted ?? false}
            aria-label={label}
            title={r.names?.join(", ")}
            onClick={() => onReact(messageId, r.emoji)}
            className={cn(cls, "hover:border-strong", HOVER_EASE, FOCUS_RING)}
          >
            <span aria-hidden={true}>{r.emoji}</span>
            <span aria-hidden={true}>{r.count}</span>
          </button>
        ) : (
          <span key={r.emoji} role="img" aria-label={label} className={cls}>
            <span aria-hidden={true}>{r.emoji}</span>
            <span aria-hidden={true}>{r.count}</span>
          </span>
        );
      })}
      {/* CHAT-SWITCH-A (Slack): another reaction from the row itself. */}
      {onReact ? (
        <EmojiPickerPopover
          label="Add reaction"
          align="start"
          onPick={(emoji) => onReact(messageId, emoji)}
          trigger={
            <button
              type="button"
              aria-label="Add reaction"
              title="Add reaction"
              className={cn(
                "inline-flex h-6 items-center rounded-full border border-border bg-card px-1.5 text-muted-foreground hover:border-strong hover:text-foreground",
                HOVER_EASE,
                FOCUS_RING,
              )}
            >
              <SmilePlusIcon aria-hidden={true} className="size-3.5" />
            </button>
          }
        />
      ) : null}
    </div>
  );
}

/** Long messages fold at this height (CHAT-UX: Slack's "Show more"). */
const FOLD_PX = 360;

/** The body: a long one is folded with a "Show more" until opened. */
function CollapsibleBody({ children }: { children: React.ReactNode }) {
  const ref = React.useRef<HTMLDivElement>(null);
  const [long, setLong] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el || open) return;
    setLong(el.scrollHeight > FOLD_PX + 40);
  }, [open]);
  const folded = long && !open;
  return (
    <div data-slot="message-body" className="text-base leading-6 text-foreground">
      <div
        ref={ref}
        data-folded={folded || undefined}
        className={folded ? "relative max-h-[360px] overflow-hidden" : undefined}
      >
        {children}
        {folded ? (
          <div
            aria-hidden={true}
            className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-linear-to-t from-card to-transparent"
          />
        ) : null}
      </div>
      {long ? (
        <Button
          variant="link"
          size="xs"
          className="h-6 px-0"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        >
          {open ? "Show less" : "Show more"}
        </Button>
      ) : null}
    </div>
  );
}

/** "Riley", "Riley and Sam", "Riley, Sam and Ana". */
function listNames(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

function ThreadSummary({
  message: m,
  now,
  locale,
  onOpenThread,
}: {
  message: ChatMessage;
  now: Date;
  locale?: string | undefined;
  onOpenThread?: ((messageId: string) => void) | undefined;
}) {
  const t = m.thread;
  if (!t) return null;
  const text =
    t.replyCount > 0 ? `${t.replyCount} ${t.replyCount === 1 ? "reply" : "replies"}` : null;
  const replying = t.replying?.length
    ? `${listNames(t.replying)} ${t.replying.length === 1 ? "is" : "are"} replying…`
    : null;
  const inner = (
    <>
      {t.participants?.length ? (
        <AvatarStack size="xs" items={t.participants.map(toAvatar)} />
      ) : null}
      {text ? <span className="font-medium text-primary-text">{text}</span> : null}
      {replying ? (
        <span data-slot="message-thread-replying" className="text-muted-foreground italic">
          {replying}
        </span>
      ) : null}
      {t.newCount ? (
        <span
          data-slot="message-thread-new"
          className="rounded-full bg-primary px-1.5 text-2xs leading-4 font-semibold text-primary-foreground tabular-nums"
        >
          {t.newCount} new
        </span>
      ) : t.unread ? (
        <>
          <span aria-hidden={true} className="size-1.5 rounded-full bg-primary" />
          <span className="sr-only">unread</span>
        </>
      ) : null}
      {t.lastReplyAt ? (
        <span className="text-muted-foreground">
          Last reply {relativeTime(t.lastReplyAt, now, locale)}
        </span>
      ) : null}
    </>
  );
  const cls = "mt-1 -ml-1 inline-flex h-7 w-fit items-center gap-2 rounded-md px-1 text-xs";
  return onOpenThread ? (
    <button
      type="button"
      data-slot="message-thread-summary"
      onClick={() => onOpenThread(m.id)}
      className={cn(cls, "hover:bg-card", HOVER_EASE, FOCUS_RING)}
    >
      {inner}
    </button>
  ) : (
    <span data-slot="message-thread-summary" className={cls}>
      {inner}
    </span>
  );
}
